import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useAuth } from '@/hooks/useAuth';
import { useAuthErrors } from '@/hooks/useAuthErrors';
import { supabase } from '@/integrations/supabase/client';
import { ArrowLeft, Mail, Lock, User, Zap, Wand2, CheckCircle, AlertCircle, Loader2, ChevronDown, Bug, Database } from 'lucide-react';

type AuthMode = 'signin' | 'signup' | 'reset-password' | 'verify-email' | 'update-password';

// Diagnostics state for debugging signup issues
interface SignupDiagnostics {
  timestamp: string;
  email: string;
  status: 'pending' | 'success' | 'error';
  userId?: string;
  emailConfirmed?: boolean;
  errorMessage?: string;
  supabaseProjectUrl: string;
}
export default function Auth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode') as AuthMode || searchParams.get('tab') as AuthMode || 'signin';
  const {
    user,
    signIn,
    signUp,
    resetPassword,
    resendEmailVerification,
    isEmailVerified
  } = useAuth();
  const {
    error,
    handleError,
    clearError,
    retryLastOperation,
    canRetry,
    isRetrying
  } = useAuthErrors();
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [signupDiagnostics, setSignupDiagnostics] = useState<SignupDiagnostics | null>(null);

  // Form states
  const [signInForm, setSignInForm] = useState({
    email: '',
    password: ''
  });
  const [signUpForm, setSignUpForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  });
  const [resetForm, setResetForm] = useState({
    email: ''
  });
  const [updatePasswordForm, setUpdatePasswordForm] = useState({
    password: '',
    confirmPassword: ''
  });

  // Redirect authenticated users
  useEffect(() => {
    if (user && user.email && !user.is_anonymous) {
      const from = searchParams.get('from') || '/';
      navigate(from, {
        replace: true
      });
    }
  }, [user, navigate, searchParams]);

  // Handle auth mode changes
  useEffect(() => {
    clearError();
    setSuccessMessage(null);
  }, [mode, clearError]);
  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);
    clearError();
    try {
      const {
        data,
        error
      } = await signIn(signInForm.email, signInForm.password);
      if (error) {
        handleError(error, 'sign in');
      } else if (data.user) {
        // Let the auth-state effect redirect after user/session state is fully hydrated.
      }
    } catch (err) {
      handleError(err, 'sign in');
    } finally {
      setIsLoading(false);
    }
  };
  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);
    clearError();

    // Validation
    if (signUpForm.password !== signUpForm.confirmPassword) {
      handleError({
        message: 'Passwords do not match'
      }, 'sign up');
      setIsLoading(false);
      return;
    }
    if (signUpForm.password.length < 6) {
      handleError({
        message: 'Password must be at least 6 characters long'
      }, 'sign up');
      setIsLoading(false);
      return;
    }
    try {
      console.log('[TailorWiz Auth] Starting signup for:', signUpForm.email);
      
      const {
        data,
        error
      } = await signUp(signUpForm.email, signUpForm.password, {
        first_name: signUpForm.firstName,
        last_name: signUpForm.lastName
      });
      
      // Update diagnostics
      const diagnostics: SignupDiagnostics = {
        timestamp: new Date().toISOString(),
        email: signUpForm.email,
        status: error ? 'error' : 'success',
        userId: data?.user?.id,
        emailConfirmed: data?.user?.email_confirmed_at != null,
        errorMessage: error?.message,
        supabaseProjectUrl: 'https://gxatnnzwaggcvzzurgsq.supabase.co'
      };
      setSignupDiagnostics(diagnostics);
      console.log('[TailorWiz Auth] Signup result:', diagnostics);
      
      if (error) {
        handleError(error, 'sign up');
      } else {
        // Enhanced success message with email instructions
        setSuccessMessage(
          `✅ Account created for ${signUpForm.email}!\n\n` +
          `📧 A verification LINK (not a code) has been sent to your email.\n\n` +
          `⚠️ IMPORTANT: Check your SPAM, Promotions, and Updates folders!\n\n` +
          `The email comes from: noreply@mail.supabase.io or similar.\n\n` +
          `Click the link in the email to verify, then come back and sign in.`
        );
        // Don't auto-redirect - let user read the instructions
      }
    } catch (err) {
      console.error('[TailorWiz Auth] Signup error:', err);
      handleError(err, 'sign up');
    } finally {
      setIsLoading(false);
    }
  };
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);
    clearError();
    try {
      const {
        data,
        error
      } = await resetPassword(resetForm.email);
      if (error) {
        handleError(error, 'password reset');
      } else {
        setSuccessMessage("Password reset instructions have been sent to your email address.");
      }
    } catch (err) {
      handleError(err, 'password reset');
    } finally {
      setIsLoading(false);
    }
  };
  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setIsLoading(true);
    clearError();
    if (updatePasswordForm.password !== updatePasswordForm.confirmPassword) {
      handleError({
        message: 'Passwords do not match'
      }, 'password update');
      setIsLoading(false);
      return;
    }
    if (updatePasswordForm.password.length < 6) {
      handleError({
        message: 'Password must be at least 6 characters long'
      }, 'password update');
      setIsLoading(false);
      return;
    }
    try {
      const {
        data,
        error
      } = await supabase.auth.updateUser({
        password: updatePasswordForm.password
      });
      if (error) {
        handleError(error, 'password update');
      } else {
        setSuccessMessage("Password updated successfully! You can now sign in with your new password.");
        setTimeout(() => {
          navigate('/auth?mode=signin');
        }, 2000);
      }
    } catch (err) {
      handleError(err, 'password update');
    } finally {
      setIsLoading(false);
    }
  };
  const handleResendVerification = async () => {
    setIsLoading(true);
    clearError();
    try {
      const {
        data,
        error
      } = await resendEmailVerification();
      if (error) {
        handleError(error, 'email verification');
      } else {
        setSuccessMessage("Verification email sent! Please check your inbox.");
      }
    } catch (err) {
      handleError(err, 'email verification');
    } finally {
      setIsLoading(false);
    }
  };
  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    clearError();
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`,
          scopes: 'openid profile email'
        }
      });
      if (error) {
        handleError(error, 'Google sign in');
      }
    } catch (err) {
      handleError(err, 'Google sign in');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFacebookSignIn = async () => {
    setIsLoading(true);
    clearError();
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'facebook',
        options: {
          redirectTo: `${window.location.origin}/`,
          scopes: 'email public_profile'
        }
      });
      if (error) {
        handleError(error, 'Facebook sign in');
      }
    } catch (err) {
      handleError(err, 'Facebook sign in');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLinkedInSignIn = async () => {
    setIsLoading(true);
    clearError();
    try {
      const {
        data,
        error
      } = await supabase.auth.signInWithOAuth({
        provider: 'linkedin_oidc',
        options: {
          redirectTo: `${window.location.origin}/`,
          scopes: 'openid profile email'
        }
      });
      if (error) {
        handleError(error, 'LinkedIn sign in');
      }
    } catch (err) {
      handleError(err, 'LinkedIn sign in');
    } finally {
      setIsLoading(false);
    }
  };
  const renderHeader = () => <div className="text-center mb-8">
      <button
        type="button"
        onClick={() => navigate('/')}
        className="mb-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-300 hover:text-[#C9A84C] transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Home
      </button>

      <div className="flex items-center justify-center gap-2 mb-2">
        <div className="w-9 h-9 rounded-lg bg-[#0B1C3F] border border-[#C9A84C]/40 flex items-center justify-center">
          <Wand2 className="w-[18px] h-[18px] text-[#C9A84C]" />
        </div>
        <h1 className="text-2xl font-extrabold tracking-tight text-white">
          Tailor<span className="text-[#C9A84C]">Wiz</span>
        </h1>
      </div>

      {mode === 'verify-email' && <Badge variant="outline" className="mt-3 border-[#C9A84C]/50 text-[#C9A84C]">
          <AlertCircle className="w-3 h-3 mr-1" />
          Email Verification Required
        </Badge>}
    </div>;
  const renderMessages = () => <>
      {error && <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>{error.message}</span>
            {canRetry && <Button variant="outline" size="sm" onClick={() => retryLastOperation()} disabled={isRetrying}>
                {isRetrying ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Retry'}
              </Button>}
          </AlertDescription>
        </Alert>}
      
      {successMessage && <Alert className="mb-4 border-green-500 bg-green-50 dark:bg-green-950/30">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="whitespace-pre-line text-sm">{successMessage}</AlertDescription>
        </Alert>}
    </>;
  if (mode === 'verify-email') {
    return <div className="min-h-screen bg-gradient-to-b from-[#0B1C3F] via-[#0E2350] to-[#0B1C3F] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {renderHeader()}
          
          <Card>
            <CardHeader className="text-center">
              <CardTitle>Verify Your Email</CardTitle>
              <CardDescription>
                We've sent a verification link to your email address. Please check your inbox and click the link to verify your account.
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-4">
              {renderMessages()}
              
              <div className="text-center space-y-4">
                <Button onClick={handleResendVerification} disabled={isLoading} className="w-full">
                  {isLoading ? <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </> : 'Resend Verification Email'}
                </Button>
                
                <Button variant="outline" onClick={() => navigate('/auth?mode=signin')} className="w-full">
                  Back to Sign In
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>;
  }
  if (mode === 'reset-password') {
    return <div className="min-h-screen bg-gradient-to-b from-[#0B1C3F] via-[#0E2350] to-[#0B1C3F] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {renderHeader()}
          
          <Card>
            <CardHeader className="text-center">
              <CardTitle>Reset Password</CardTitle>
              <CardDescription>
                Enter your email address and we'll send you a link to reset your password.
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              {renderMessages()}
              
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input id="reset-email" type="email" placeholder="Enter your email" value={resetForm.email} onChange={e => setResetForm(prev => ({
                    ...prev,
                    email: e.target.value
                  }))} className="pl-10" required />
                  </div>
                </div>
                
                <Button type="submit" className="w-full bg-[#C9A84C] text-[#0B1C3F] hover:bg-[#E5C36A] font-bold" disabled={isLoading}>
                  {isLoading ? <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending Reset Link...
                    </> : 'Send Reset Link'}
                </Button>
                
                <Button type="button" variant="outline" onClick={() => navigate('/auth?mode=signin')} className="w-full">
                  Back to Sign In
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>;
  }
  if (mode === 'update-password') {
    return <div className="min-h-screen bg-gradient-to-b from-[#0B1C3F] via-[#0E2350] to-[#0B1C3F] flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {renderHeader()}
          
          <Card>
            <CardHeader className="text-center">
              <CardTitle>Update Password</CardTitle>
              <CardDescription>
                Enter your new password below.
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              {renderMessages()}
              
              <form onSubmit={handleUpdatePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input id="new-password" type="password" placeholder="Enter new password" value={updatePasswordForm.password} onChange={e => setUpdatePasswordForm(prev => ({
                    ...prev,
                    password: e.target.value
                  }))} className="pl-10" required />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirm-new-password">Confirm New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input id="confirm-new-password" type="password" placeholder="Confirm new password" value={updatePasswordForm.confirmPassword} onChange={e => setUpdatePasswordForm(prev => ({
                    ...prev,
                    confirmPassword: e.target.value
                  }))} className="pl-10" required />
                  </div>
                </div>
                
                <Button type="submit" className="w-full bg-[#C9A84C] text-[#0B1C3F] hover:bg-[#E5C36A] font-bold" disabled={isLoading}>
                  {isLoading ? <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Updating Password...
                    </> : 'Update Password'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-gradient-to-b from-[#0B1C3F] via-[#0E2350] to-[#0B1C3F] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {renderHeader()}
        
        <Card>
          <CardHeader>
            <CardTitle>Welcome</CardTitle>
            <CardDescription>
              Sign in to your account to get started
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <Tabs value="signin" className="w-full">
              <TabsList className="grid w-full grid-cols-1">
                <TabsTrigger value="signin" className="text-white bg-[#0B1C3F] hover:bg-[#1a2d56] data-[state=active]:bg-[#0B1C3F]">
                  Sign In
                </TabsTrigger>
              </TabsList>
              
              {renderMessages()}
              
              <TabsContent value="signin" className="space-y-4 mt-4">
                <form onSubmit={handleSignIn} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input id="signin-email" type="email" placeholder="Enter your email" value={signInForm.email} onChange={e => setSignInForm(prev => ({
                      ...prev,
                      email: e.target.value
                    }))} className="pl-10" required />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="signin-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input id="signin-password" type="password" placeholder="Enter your password" value={signInForm.password} onChange={e => setSignInForm(prev => ({
                      ...prev,
                      password: e.target.value
                    }))} className="pl-10" required />
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <Button type="button" variant="link" size="sm" onClick={() => navigate('/auth?mode=reset-password')} className="p-0 h-auto">
                      Forgot password?
                    </Button>
                  </div>
                  
                  <Button type="submit" className="w-full bg-[#C9A84C] text-[#0B1C3F] hover:bg-[#E5C36A] font-bold" disabled={isLoading}>
                    {isLoading ? <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Signing In...
                      </> : 'Sign In'}
                  </Button>
                </form>
                
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
                  </div>
                </div>
                
                <Button onClick={handleGoogleSignIn} variant="outline" className="w-full" disabled={isLoading}>
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>

                <Button onClick={handleFacebookSignIn} variant="outline" className="w-full" disabled={isLoading}>
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="#1877F2">
                    <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07c0 6.03 4.39 11.03 10.13 11.93v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.69.24 2.69.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z" />
                  </svg>
                  Continue with Facebook
                </Button>

                <Button onClick={handleLinkedInSignIn} variant="outline" className="w-full" disabled={isLoading}>
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                  Continue with LinkedIn
                </Button>
              </TabsContent>
              
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>;
}